import { appConfig } from '@/config/environment'
import { userNavigationItems } from '@/config/userNavigation'
import type { PermissionedMenuItem } from '@/types/permission'
import { getCurrentTenantCode } from '@/utils/tenantRoute'

/**
 * 北极星埋点平台 SDK 接入。
 *
 * index.html 中已异步加载 point-sdk.min.js（stub 队列机制），此处负责在
 * 应用启动时生成菜单映射并完成 init；未配置 serverUrl / appKey 时静默跳过，
 * SDK 加载失败时 index.html 的 onerror 兜底会降级为 no-op，均不影响业务。
 */

export interface PointTrackerMenuEntry {
  code: string
  name: string
}

export type PointTrackerMenuMapping = Record<string, PointTrackerMenuEntry>

export interface PointTrackerInitConfig {
  appKey: string
  serverUrl: string
  /** 菜单识别方式二选一：menuMapping（路由映射）与 menuSelector（CSS 选择器） */
  menuMapping?: PointTrackerMenuMapping
  menuSelector?: string
  /** storage 中保存用户信息的 key */
  userInfoKey: string
  /** 用户唯一标识字段名 */
  userIdField: string
  /** 组织字段名（可选） */
  orgField?: string
  /** 岗位字段名（可选） */
  positionField?: string
  debug?: boolean
  batchSize?: number
  batchInterval?: number
}

export interface PointTrackerApi {
  init(config: PointTrackerInitConfig): void
  track(event: string, params?: Record<string, unknown>): void
  /** userId 为用户唯一标识，customFields 为随事件上报的扩展字段 */
  setUser(userId: string, customFields?: Record<string, unknown>): void
  setCustomFields(fields: Record<string, unknown>): void
  flush(): void
}

declare global {
  interface Window {
    PointTracker?: PointTrackerApi
  }
}

/**
 * 基于导航菜单配置生成 menuMapping（路由路径 → 菜单编码/名称）。
 *
 * 多租户路径模式下同一菜单同时注册基础路径与 /t/{tenantCode} 前缀变体，
 * 覆盖子域名模式（无前缀）与路径模式两种 URL 形态；activePrefix 一并注册，
 * 保证菜单下的子页面也能命中映射。
 */
export function buildMenuMapping(
  items: PermissionedMenuItem[] = userNavigationItems,
  tenantCode = getCurrentTenantCode()
): PointTrackerMenuMapping {
  const mapping: PointTrackerMenuMapping = {}
  for (const item of items) {
    const paths = new Set<string>()
    if (item.route) paths.add(item.route)
    if (item.path) paths.add(item.path)
    if (item.activePrefix && item.activePrefix !== item.route && item.activePrefix !== item.path) {
      paths.add(item.activePrefix)
    }
    if (paths.size === 0) continue
    const entry: PointTrackerMenuEntry = { code: item.key, name: item.label || item.title }
    for (const path of paths) {
      mapping[path] = entry
      if (tenantCode) {
        mapping[`/t/${tenantCode}${path}`] = entry
      }
    }
  }
  return mapping
}

/**
 * 计算埋点上报的用户唯一标识（email 的 @ 前缀）。
 *
 * 平台侧用户名称通过 JOIN pt_user 表关联展示，userId 需为可读账号：
 * 优先取 email 的 @ 前缀（如 zhuxiaofan01@semir.com → zhuxiaofan01），
 * email 缺失或格式非法时回退 username。
 */
export function resolveEmailPrefix(user: { username?: string; email?: string }): string {
  const emailPrefix = user.email?.split('@')[0]?.trim()
  if (emailPrefix && user.email?.includes('@')) return emailPrefix
  return user.username ?? ''
}

/**
 * 为 user 对象补齐 emailPrefix 字段（纯函数）。
 *
 * SDK 的 autoDetectUser 会在每次 localStorage.setItem('user', ...) 时
 * 以 userIdField 重新覆盖埋点 userId。为了让 userId 始终为 email 前缀，
 * 所有业务代码写入 user 之前都应调用此函数注入 emailPrefix 字段。
 *
 * 已有 emailPrefix 的对象原样返回，不会重复计算。
 */
export function enrichEmailPrefix<T extends { emailPrefix?: string; username?: string; email?: string }>(
  user: T
): T {
  if (user.emailPrefix) return user
  const prefix = resolveEmailPrefix(user)
  return prefix ? { ...user, emailPrefix: prefix } : user
}

/**
 * 启动时补齐 storage 中 user 的埋点字段。
 *
 * - 读取 localStorage.user，注入 emailPrefix（存量用户兼容）；
 * - 将 nickname 以 customFields.userName 上报供平台展示。
 *
 * 存量用户兼容原理：主动写回 localStorage 会触发 SDK 的 setItem hook
 * （autoDetectUser 机制），SDK 会用 userIdField='emailPrefix' 重新提取
 * userId，从而让后续埋点的 userId 稳定为 email 前缀。
 */
function ensureUserStorageReady(): void {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return
    const user = JSON.parse(raw) as {
      nickname?: string
      emailPrefix?: string
      username?: string
      email?: string
    }
    if (user.nickname) {
      window.PointTracker?.setCustomFields({ userName: user.nickname })
    }
    // 存量用户兼容：无 emailPrefix 时主动写回，触发 SDK 重新提取 userId
    if (!user.emailPrefix) {
      const enriched = enrichEmailPrefix(user)
      if (enriched !== user) {
        localStorage.setItem('user', JSON.stringify(enriched))
      }
    }
  } catch {
    // 用户信息解析失败不影响埋点主流程
  }
}

/**
 * 应用启动时初始化埋点 SDK。
 *
 * 用户信息约定：
 * - userId 由 SDK 通过 userInfoKey='user' + userIdField='emailPrefix'
 *   自动从 storage 提取（业务写入 user 前必须调用 enrichEmailPrefix）；
 * - 昵称以 customFields.userName 上报供平台展示。
 */
export function initPointTracker(): void {
  const { trackServerUrl, trackAppKey, trackDebug } = appConfig
  if (!trackServerUrl || !trackAppKey) return
  try {
    window.PointTracker?.init({
      appKey: trackAppKey,
      serverUrl: trackServerUrl,
      menuMapping: buildMenuMapping(),
      userInfoKey: 'user',
      userIdField: 'emailPrefix',
      debug: trackDebug,
    })
    // 启动时补齐 emailPrefix（存量用户兼容）并补充上报用户名称
    ensureUserStorageReady()
  } catch (error) {
    // SDK 初始化异常不允许影响业务应用启动，记录后继续
    console.warn('[PointTracker] init failed, tracking disabled:', error)
  }
}
